import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// Detect if running as native app (APK)
const isNativeApp = Capacitor.isNativePlatform();

// Backend URL: Always use env variable or current origin (no hardcoded URLs)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const API_URL = `${BACKEND_URL}/api`;

// Log for debugging
if (typeof window !== 'undefined') {
  console.log('[API] Platform:', isNativeApp ? 'Native (APK)' : 'Web');
  console.log('[API] Backend URL:', BACKEND_URL);
}

// === Cookie helpers for reliable token backup ===
function setTokenCookie(token) {
  if (typeof document !== 'undefined') {
    const maxAge = 30 * 24 * 60 * 60; // 30 days
    document.cookie = `auth_token=${token}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }
}

function getTokenCookie() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|; )auth_token=([^;]*)/);
  return match ? match[1] : null;
}

function clearTokenCookie() {
  if (typeof document !== 'undefined') {
    document.cookie = 'auth_token=; path=/; max-age=0';
  }
}

// === Module-level token cache ===
// Read from multiple sources at startup for maximum reliability
let cachedToken = localStorage.getItem('token') || getTokenCookie() || null;

// Flag: true while AuthContext is running checkAuth. Prevents 401 handler from wiping tokens.
let isCheckingAuth = false;

export const setAuthCheckingState = (checking) => {
  isCheckingAuth = checking;
};

// Update cached token (called by AuthContext)
export const setApiToken = (token) => {
  cachedToken = token;
  if (token) {
    setTokenCookie(token);
  }
};

// Clear all token storage
export const clearApiToken = () => {
  cachedToken = null;
  clearTokenCookie();
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Add token to requests using cached token (synchronous, no timing issues)
api.interceptors.request.use(
  (config) => {
    if (cachedToken) {
      config.headers.Authorization = `Bearer ${cachedToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors - but NOT during auth initialization
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // CRITICAL: Don't clear tokens during initial auth check.
      // AuthContext handles re-auth and will explicitly clear if the token is truly invalid.
      if (isCheckingAuth) {
        console.log('[API] 401 during auth check - NOT clearing tokens (AuthContext will handle)');
        return Promise.reject(error);
      }
      
      // Only clear for explicit user-initiated requests that fail
      console.log('[API] 401 on user request - clearing auth state');
      cachedToken = null;
      clearTokenCookie();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (isNativeApp) {
        try {
          await Preferences.remove({ key: 'token' });
          await Preferences.remove({ key: 'user' });
        } catch (e) {
          console.error('Failed to clear Preferences:', e);
        }
      }
    }
    return Promise.reject(error);
  }
);

// ── Offline-aware wrappers ───────────────────────────────
import {
  offlineModulesAPI, offlineLessonsAPI, offlineCycleAPI,
  offlineContraceptionAPI, offlineNotificationsAPI,
  offlineGamificationAPI, offlineGirlsRoomAPI,
  offlineCommunityAPI, offlineTipsAPI, offlinePartnersAPI,
  offlineAlertsAPI, offlineUserAPI, offlineRemindersAPI,
  offlineQuizAPI,
} from './offlineApi';

/**
 * offlineRead — For GET endpoints.
 *   Offline → read from Dexie immediately (skip network)
 *   Online  → call API, then cache response into Dexie, return API data
 */
function offlineRead(axiosCall, localReadFn, cacheFn) {
  return async (...args) => {
    if (!navigator.onLine) {
      const local = await localReadFn(...args);
      return { data: local, _offline: true };
    }
    try {
      const response = await axiosCall(...args);
      // Cache the successful response into Dexie (fire-and-forget)
      if (cacheFn) {
        try { await cacheFn(response.data, ...args); } catch (e) { console.warn('[API] Cache write failed:', e.message); }
      }
      return response;
    } catch (error) {
      if (!error.response) {
        // Network error despite navigator.onLine (e.g. server down)
        const local = await localReadFn(...args);
        return { data: local, _offline: true };
      }
      throw error;
    }
  };
}

/**
 * offlineWrite — For POST/PUT/DELETE endpoints.
 *   Offline → write to Dexie optimistically + queue for sync
 *   Online  → call API, then update Dexie with response
 */
function offlineWrite(axiosCall, localWriteFn, onSuccessCacheFn) {
  return async (...args) => {
    if (!navigator.onLine) {
      const local = await localWriteFn(...args);
      return { data: local, _offline: true };
    }
    try {
      const response = await axiosCall(...args);
      if (onSuccessCacheFn) {
        try { await onSuccessCacheFn(response.data, ...args); } catch (e) { console.warn('[API] Cache update failed:', e.message); }
      }
      return response;
    } catch (error) {
      if (!error.response) {
        const local = await localWriteFn(...args);
        return { data: local, _offline: true };
      }
      throw error;
    }
  };
}

// ============================================================
// Auth APIs (no offline — requires server)
// ============================================================
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: offlineRead(
    () => api.get('/auth/me'),
    async () => {
      const user = await offlineUserAPI.getProfile();
      return user ? { user } : null;
    },
    async (data) => { if (data?.user) await offlineUserAPI.cacheProfile(data.user); }
  ),
};

// ============================================================
// User APIs
// ============================================================
export const userAPI = {
  getProfile: offlineRead(
    () => api.get('/users/profile'),
    async () => ({ user: (await offlineUserAPI.getProfile()) || {} }),
    async (data) => { if (data?.user) await offlineUserAPI.cacheProfile(data.user); }
  ),
  getFullProfile: offlineRead(
    () => api.get('/users/profile/full'),
    async () => (await offlineUserAPI.getFullProfile()) || { user: {}, stats: {}, badges: [] },
    async (data) => { await offlineUserAPI.cacheFullProfile(data); }
  ),
  updateProfile: offlineWrite(
    (data) => api.put('/users/profile', data),
    (data) => offlineUserAPI.updateProfile(data),
    async (data) => { if (data?.user) await offlineUserAPI.cacheProfile(data.user); }
  ),
  checkIn: offlineWrite(
    () => api.post('/users/checkin'),
    () => offlineUserAPI.checkIn(),
    null
  ),
};

// ============================================================
// Modules APIs
// ============================================================
export const modulesAPI = {
  getAll: offlineRead(
    (category) => api.get('/modules', { params: { category } }),
    async () => ({ modules: await offlineModulesAPI.getAll() }),
    async (data) => { await offlineModulesAPI.cacheModules(data?.modules); }
  ),
  getById: offlineRead(
    (id) => api.get(`/modules/${id}`),
    async (id) => ({ module: await offlineModulesAPI.getById(id) }),
    async (data) => { await offlineModulesAPI.cacheModule(data?.module); }
  ),
  getLessons: offlineRead(
    (moduleId) => api.get(`/modules/${moduleId}/lessons`),
    async (moduleId) => {
      const lessons = await offlineModulesAPI.getLessons(moduleId);
      return { lessons, totalLessons: lessons.length, completedCount: lessons.filter(l => l.completed).length };
    },
    async (data) => { await offlineModulesAPI.cacheLessons(data?.lessons); }
  ),
  getQuiz: offlineRead(
    (moduleId) => api.get(`/modules/${moduleId}/quiz`),
    async (moduleId) => ({ quiz: await offlineModulesAPI.getQuiz(moduleId) }),
    async (data) => { await offlineModulesAPI.cacheQuiz(data?.quiz); }
  ),
};

// ============================================================
// Lessons APIs
// ============================================================
export const lessonsAPI = {
  getById: offlineRead(
    (id) => api.get(`/lessons/${id}`),
    async (id) => ({ lesson: await offlineLessonsAPI.getById(id) }),
    async (data) => { await offlineLessonsAPI.cacheLesson(data?.lesson); }
  ),
  complete: offlineWrite(
    (id) => api.post(`/lessons/${id}/complete`),
    (id) => offlineLessonsAPI.complete(id),
    null
  ),
};

// ============================================================
// Quiz APIs
// ============================================================
export const quizAPI = {
  submit: offlineWrite(
    (quizId, answers) => api.post(`/quizzes/${quizId}/submit`, { answers }),
    (quizId, answers) => offlineQuizAPI.submit(quizId, answers),
    null
  ),
};

// ============================================================
// Contraception APIs
// ============================================================
export const contraceptionAPI = {
  get: offlineRead(
    () => api.get('/contraception'),
    async () => ({ contraceptions: await offlineContraceptionAPI.get() }),
    async (data) => { await offlineContraceptionAPI.cacheContraceptions(data?.contraceptions); }
  ),
  create: offlineWrite(
    (data) => api.post('/contraception', data),
    (data) => offlineContraceptionAPI.create(data),
    async (data) => { if (data?.contraception) await offlineContraceptionAPI.cacheContraceptions([data.contraception]); }
  ),
  update: offlineWrite(
    (id, data) => api.put(`/contraception/${id}`, data),
    (id, data) => offlineContraceptionAPI.update(id, data),
    async (data) => { if (data?.contraception) await offlineContraceptionAPI.cacheContraceptions([data.contraception]); }
  ),
  delete: offlineWrite(
    (id) => api.delete(`/contraception/${id}`),
    (id) => offlineContraceptionAPI.remove(id),
    null
  ),
  recordTaken: offlineWrite(
    (data = {}) => api.post('/contraception/take', data),
    (data = {}) => offlineContraceptionAPI.recordTaken(data),
    null
  ),
  getHistory: offlineRead(
    (days = 30) => api.get(`/contraception/history?days=${days}`),
    async () => ({ history: await offlineContraceptionAPI.getHistory() }),
    async (data) => { await offlineContraceptionAPI.cacheLogs(data?.history); }
  ),
  getStats: offlineRead(
    () => api.get('/contraception/stats'),
    () => offlineContraceptionAPI.getStats(),
    null
  ),
};

// ============================================================
// Cycle APIs
// ============================================================
export const cycleAPI = {
  get: offlineRead(
    () => api.get('/cycle'),
    async () => {
      const cycle = await offlineCycleAPI.get();
      return { cycle, predictions: cycle?.predictions || [] };
    },
    async (data) => { if (data?.cycle) await offlineCycleAPI.cacheCycle(data.cycle); }
  ),
  create: offlineWrite(
    (data) => api.post('/cycle', data),
    (data) => offlineCycleAPI.create(data),
    async (data) => { if (data?.cycle) await offlineCycleAPI.cacheCycle(data.cycle); }
  ),
  update: offlineWrite(
    (data) => api.put('/cycle', data),
    (data) => offlineCycleAPI.update(data),
    async (data) => { if (data?.cycle) await offlineCycleAPI.cacheCycle(data.cycle); }
  ),
  getHistory: offlineRead(
    (months = 12) => api.get(`/cycle/history?months=${months}`),
    async () => ({ history: await offlineCycleAPI.getHistory(), stats: {} }),
    async (data) => { await offlineCycleAPI.cacheHistory(data?.history); }
  ),
  recordPeriod: offlineWrite(
    (data) => api.post('/cycle/history/record', data),
    (data) => offlineCycleAPI.recordPeriod(data),
    null
  ),
  deleteRecord: offlineWrite(
    (id) => api.delete(`/cycle/history/${id}`),
    (id) => offlineCycleAPI.deleteRecord(id),
    null
  ),
  updateRecord: offlineWrite(
    (id, data) => api.put(`/cycle/history/${id}`, data),
    (id, data) => offlineCycleAPI.updateRecord(id, data),
    null
  ),
  getCalendarData: offlineRead(
    (year, month) => api.get(`/cycle/history/calendar?year=${year}&month=${month}`),
    (year, month) => offlineCycleAPI.getCalendarData(year, month),
    null
  ),
};

// ============================================================
// Notifications APIs
// ============================================================
export const notificationsAPI = {
  getAll: offlineRead(
    () => api.get('/notifications'),
    async () => ({ notifications: await offlineNotificationsAPI.getAll() }),
    async (data) => { await offlineNotificationsAPI.cacheNotifications(data?.notifications); }
  ),
  markAsRead: offlineWrite(
    (id) => api.put(`/notifications/${id}/read`),
    (id) => offlineNotificationsAPI.markAsRead(id),
    null
  ),
  delete: offlineWrite(
    (id) => api.delete(`/notifications/${id}`),
    (id) => offlineNotificationsAPI.remove(id),
    null
  ),
  getPushStatus: () => api.get('/notifications/push-status'),
  testPush: () => api.post('/notifications/test-push'),
  getDiagnostics: () => api.get('/notifications/push-diagnostics'),
  registerDevice: (token, platform) => api.post('/notifications/register-device', { token, platform }),
};

// ============================================================
// Alerts APIs
// ============================================================
export const alertsAPI = {
  getSettings: offlineRead(
    () => api.get('/alerts/settings'),
    async () => ({ settings: (await offlineAlertsAPI.getSettings()) || {} }),
    async (data) => { await offlineAlertsAPI.cacheSettings(data?.settings); }
  ),
  updateSettings: offlineWrite(
    (data) => api.put('/alerts/settings', data),
    (data) => offlineAlertsAPI.updateSettings(data),
    null
  ),
  createCustom: offlineWrite(
    (data) => api.post('/alerts/create-custom', data),
    (data) => offlineAlertsAPI.createCustom(data),
    null
  ),
  getCustom: offlineRead(
    () => api.get('/alerts/custom'),
    async () => ({ alerts: await offlineAlertsAPI.getCustom() }),
    async (data) => { await offlineAlertsAPI.cacheCustomAlerts(data?.alerts); }
  ),
  deleteCustom: offlineWrite(
    (id) => api.delete(`/alerts/custom/${id}`),
    (id) => offlineAlertsAPI.deleteCustom(id),
    null
  ),
  toggleCustom: offlineWrite(
    (id) => api.put(`/alerts/custom/${id}/toggle`),
    (id) => offlineAlertsAPI.toggleCustom(id),
    null
  ),
  getPreview: offlineRead(
    () => api.get('/alerts/preview'),
    async () => ({ alerts: [] }),
    null
  ),
  getLogs: (limit = 50, alertType = null) => api.get(`/alerts/logs?limit=${limit}${alertType ? `&alert_type=${alertType}` : ''}`),
};

// ============================================================
// Partners APIs
// ============================================================
export const partnersAPI = {
  getAll: offlineRead(
    () => api.get('/partners'),
    async () => ({ partners: await offlinePartnersAPI.getAll() }),
    async (data) => { await offlinePartnersAPI.cachePartners(data?.partners); }
  ),
  getById: offlineRead(
    (id) => api.get(`/partners/${id}`),
    async (id) => ({ partner: await offlinePartnersAPI.getById(id) }),
    null
  ),
};

// ============================================================
// Seed API (online only)
// ============================================================
export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
